"use client";

import { useRef } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useInView,
  useReducedMotion,
} from "framer-motion";
import { cn } from "@/lib/utils";

const EASE = [0.22, 1, 0.36, 1] as const;

export type RevealDirection = "up" | "down" | "left" | "right" | "scale";

interface RevealProps {
  children: React.ReactNode;
  className?: string;
  /** Direction the element eases IN from. */
  direction?: RevealDirection;
  /** Travel distance in px (ignored for "scale"). */
  distance?: number;
  /** Kept for API compatibility — one-time entrances never animate out. */
  exit?: boolean;
  /** Extra delay so siblings don't move in perfect lockstep. */
  delay?: number;
}

/**
 * A smooth, one-time entrance. The element eases in from {@link direction} the
 * first time it scrolls into view, then stays put — no per-frame scroll
 * scrubbing and no jarring fade-out, which keeps scrolling buttery. Renders
 * plainly under reduced motion.
 */
export function Reveal({
  children,
  className,
  direction = "up",
  distance = 40,
  delay = 0,
}: RevealProps) {
  const reduce = useReducedMotion();
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-12%" });

  if (reduce) return <div className={className}>{children}</div>;

  const isX = direction === "left" || direction === "right";
  const sign = direction === "right" || direction === "down" ? 1 : -1;
  const dist = direction === "scale" ? 0 : distance;

  const hidden =
    direction === "scale"
      ? { opacity: 0, scale: 0.94 }
      : isX
        ? { opacity: 0, x: sign * dist }
        : { opacity: 0, y: sign * dist };
  const shown = { opacity: 1, x: 0, y: 0, scale: 1 };

  return (
    <motion.div
      ref={ref}
      className={className}
      initial={hidden}
      animate={inView ? shown : hidden}
      transition={{ duration: 0.7, ease: EASE, delay }}
    >
      {children}
    </motion.div>
  );
}

interface ParallaxProps {
  children: React.ReactNode;
  className?: string;
  /** Vertical drift in px across the element's full scroll pass. */
  distance?: number;
}

/**
 * Drifts its children vertically as the page scrolls, creating gentle depth.
 * Use sparingly — every instance recomputes on each scroll frame.
 */
export function Parallax({
  children,
  className,
  distance = 60,
}: ParallaxProps) {
  const reduce = useReducedMotion();
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [distance, -distance]);

  if (reduce) return <div className={className}>{children}</div>;

  return (
    <motion.div ref={ref} className={cn(className)} style={{ y }}>
      {children}
    </motion.div>
  );
}
